export const BROWSER_OTEL_PROXY_PATH =
  "/api/observability/otel/v1/traces";

const BROWSER_OTEL_SERVICE_NAME = "vivus-web-browser";
const BROWSER_OTEL_SCOPE_NAME = "vivus.browser.fetch";
const TRACEPARENT_VERSION = "00";
const TRACEPARENT_TRACE_FLAGS = "01";
const SPAN_KIND_CLIENT = 3;
const STATUS_CODE_OK = 1;
const STATUS_CODE_ERROR = 2;

type BrowserTracingEnv = {
  readonly VITE_ENVIRONMENT: string;
  readonly VITE_OTEL_BROWSER_EXPORT_URL?:
    | string
    | undefined;
  readonly VITE_OTEL_TRACES_ENABLED: boolean;
  readonly VITE_VERSION: string;
};

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

type BrowserLocationLike = {
  readonly href: string;
  readonly origin: string;
};

type BrowserLike = {
  fetch: FetchLike;
  location: BrowserLocationLike;
};

type BrowserTraceRecord = {
  readonly endTimeUnixNano: string;
  readonly errorMessage?: string;
  readonly kind: number;
  readonly method: string;
  readonly name: string;
  readonly spanId: string;
  readonly startTimeUnixNano: string;
  readonly statusCode?: number;
  readonly traceId: string;
  readonly url: URL;
};

type OtlpAttributeValue = {
  readonly boolValue?: boolean;
  readonly intValue?: number;
  readonly stringValue?: string;
};

type BrowserTraceExportRequest = {
  readonly resourceSpans: Array<{
    readonly resource: {
      readonly attributes: Array<{
        readonly key: string;
        readonly value: OtlpAttributeValue;
      }>;
    };
    readonly scopeSpans: Array<{
      readonly scope: {
        readonly name: string;
        readonly version: string;
      };
      readonly spans: Array<{
        readonly attributes: Array<{
          readonly key: string;
          readonly value: OtlpAttributeValue;
        }>;
        readonly endTimeUnixNano: string;
        readonly kind: number;
        readonly name: string;
        readonly spanId: string;
        readonly startTimeUnixNano: string;
        readonly status: {
          readonly code: number;
          readonly message?: string;
        };
        readonly traceId: string;
      }>;
    }>;
  }>;
};

type ResolvedBrowserTracingConfig = {
  readonly environment: string;
  readonly exportMode:
    | "public-endpoint"
    | "same-origin-proxy";
  readonly exportUrl: string;
  readonly serviceName: string;
  readonly serviceVersion: string;
};

let isBrowserOtelInstalled = false;

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
}

function createHexId(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);

  crypto.getRandomValues(bytes);

  return toHex(bytes);
}

function createUnixNanoTimestamp(
  dateNow = Date.now()
): string {
  return (BigInt(dateNow) * 1_000_000n).toString();
}

function createStringAttribute(
  key: string,
  value?: string
) {
  return value === undefined
    ? undefined
    : {
        key,
        value: {
          stringValue: value,
        },
      };
}

function createIntAttribute(key: string, value?: number) {
  return value === undefined
    ? undefined
    : {
        key,
        value: {
          intValue: value,
        },
      };
}

export function createTraceparentHeader(
  traceId: string,
  spanId: string
): string {
  return `${TRACEPARENT_VERSION}-${traceId}-${spanId}-${TRACEPARENT_TRACE_FLAGS}`;
}

export function resolveBrowserTracingConfig(
  env: BrowserTracingEnv,
  location: BrowserLocationLike
): ResolvedBrowserTracingConfig | undefined {
  if (
    !env.VITE_OTEL_TRACES_ENABLED ||
    env.VITE_OTEL_BROWSER_EXPORT_URL === undefined
  ) {
    return undefined;
  }

  const exportUrl = new URL(
    env.VITE_OTEL_BROWSER_EXPORT_URL,
    location.origin
  );

  if (
    exportUrl.protocol !== "http:" &&
    exportUrl.protocol !== "https:"
  ) {
    return undefined;
  }

  return {
    environment: env.VITE_ENVIRONMENT,
    exportMode:
      exportUrl.origin === location.origin &&
      exportUrl.pathname === BROWSER_OTEL_PROXY_PATH
        ? "same-origin-proxy"
        : "public-endpoint",
    exportUrl: exportUrl.toString(),
    serviceName: BROWSER_OTEL_SERVICE_NAME,
    serviceVersion: env.VITE_VERSION,
  };
}

export function shouldTraceBrowserRequest(
  requestUrl: URL,
  locationOrigin: string,
  exportUrl: URL
): boolean {
  if (
    requestUrl.protocol !== "http:" &&
    requestUrl.protocol !== "https:"
  ) {
    return false;
  }

  if (requestUrl.origin !== locationOrigin) {
    return false;
  }

  if (!requestUrl.pathname.startsWith("/api/")) {
    return false;
  }

  return !(
    requestUrl.origin === exportUrl.origin &&
    requestUrl.pathname === exportUrl.pathname
  );
}

export function buildBrowserTraceExportRequest(
  config: ResolvedBrowserTracingConfig,
  trace: BrowserTraceRecord
): BrowserTraceExportRequest {
  return {
    resourceSpans: [
      {
        resource: {
          attributes: [
            createStringAttribute(
              "service.name",
              config.serviceName
            ),
            createStringAttribute(
              "service.version",
              config.serviceVersion
            ),
            createStringAttribute(
              "deployment.environment.name",
              config.environment
            ),
            createStringAttribute(
              "runtime.kind",
              "browser"
            ),
            createStringAttribute(
              "vivus.observability.parallel_run",
              "sentry+otel"
            ),
            createStringAttribute(
              "vivus.observability.trace_owner",
              "tempo"
            ),
            createStringAttribute(
              "vivus.observability.export_mode",
              config.exportMode
            ),
          ].filter((attribute) => attribute !== undefined),
        },
        scopeSpans: [
          {
            scope: {
              name: BROWSER_OTEL_SCOPE_NAME,
              version: config.serviceVersion,
            },
            spans: [
              {
                attributes: [
                  createStringAttribute(
                    "http.request.method",
                    trace.method
                  ),
                  createIntAttribute(
                    "http.response.status_code",
                    trace.statusCode
                  ),
                  createStringAttribute(
                    "url.full",
                    trace.url.toString()
                  ),
                  createStringAttribute(
                    "url.path",
                    trace.url.pathname
                  ),
                  createStringAttribute(
                    "server.address",
                    trace.url.hostname
                  ),
                  createStringAttribute(
                    "error.message",
                    trace.errorMessage
                  ),
                ].filter(
                  (attribute) => attribute !== undefined
                ),
                endTimeUnixNano: trace.endTimeUnixNano,
                kind: trace.kind,
                name: trace.name,
                spanId: trace.spanId,
                startTimeUnixNano: trace.startTimeUnixNano,
                status: {
                  code:
                    trace.errorMessage === undefined
                      ? STATUS_CODE_OK
                      : STATUS_CODE_ERROR,
                  ...(trace.errorMessage === undefined
                    ? {}
                    : {
                        message: trace.errorMessage,
                      }),
                },
                traceId: trace.traceId,
              },
            ],
          },
        ],
      },
    ],
  };
}

export function createBrowserTraceExportInit(
  body: string,
  exportUrl: string,
  locationOrigin: string
): RequestInit {
  const resolvedExportUrl = new URL(exportUrl);

  return {
    body,
    credentials: "omit",
    headers: {
      "content-type": "application/json",
    },
    keepalive: true,
    method: "POST",
    mode:
      resolvedExportUrl.origin === locationOrigin
        ? "same-origin"
        : "cors",
  };
}

async function exportBrowserTrace(
  config: ResolvedBrowserTracingConfig,
  trace: BrowserTraceRecord,
  fetchImpl: FetchLike,
  locationOrigin: string
) {
  const body = JSON.stringify(
    buildBrowserTraceExportRequest(config, trace)
  );

  await fetchImpl(
    config.exportUrl,
    createBrowserTraceExportInit(
      body,
      config.exportUrl,
      locationOrigin
    )
  );
}

function createTracedRequest(
  input: RequestInfo | URL,
  traceparent: string,
  init?: RequestInit
): Request {
  const request = new Request(input, init);
  const headers = new Headers(request.headers);

  headers.set("traceparent", traceparent);

  return new Request(request, { headers });
}

export function installBrowserOtelTracing(
  env: BrowserTracingEnv,
  browser: BrowserLike = window
): boolean {
  if (isBrowserOtelInstalled) {
    return false;
  }

  const config = resolveBrowserTracingConfig(
    env,
    browser.location
  );

  if (config === undefined) {
    return false;
  }

  const exportUrl = new URL(config.exportUrl);
  const currentFetch = browser.fetch.bind(browser);

  browser.fetch = async (input, init) => {
    const request = new Request(input, init);
    const requestUrl = new URL(
      request.url,
      browser.location.href
    );

    if (
      !shouldTraceBrowserRequest(
        requestUrl,
        browser.location.origin,
        exportUrl
      ) ||
      request.headers.has("traceparent")
    ) {
      return currentFetch(input, init);
    }

    const traceId = createHexId(16);
    const spanId = createHexId(8);
    const traceparent = createTraceparentHeader(
      traceId,
      spanId
    );
    const startTimeUnixNano = createUnixNanoTimestamp();
    const tracedRequest = createTracedRequest(
      input,
      traceparent,
      init
    );

    try {
      const response = await currentFetch(tracedRequest);

      void exportBrowserTrace(
        config,
        {
          endTimeUnixNano: createUnixNanoTimestamp(),
          kind: SPAN_KIND_CLIENT,
          method: tracedRequest.method,
          name: `${tracedRequest.method} ${requestUrl.pathname}`,
          spanId,
          startTimeUnixNano,
          statusCode: response.status,
          traceId,
          url: requestUrl,
        },
        currentFetch,
        browser.location.origin
      ).catch(() => undefined);

      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : String(error);

      void exportBrowserTrace(
        config,
        {
          endTimeUnixNano: createUnixNanoTimestamp(),
          errorMessage,
          kind: SPAN_KIND_CLIENT,
          method: tracedRequest.method,
          name: `${tracedRequest.method} ${requestUrl.pathname}`,
          spanId,
          startTimeUnixNano,
          traceId,
          url: requestUrl,
        },
        currentFetch,
        browser.location.origin
      ).catch(() => undefined);

      throw error;
    }
  };

  isBrowserOtelInstalled = true;

  return true;
}
